// src/components/admin/mail/MailEmptyState.tsx
import React from 'react';
import styled from 'styled-components';
import { AlertCircle, Inbox, Mail, MailOpen, SearchX } from 'lucide-react';
import { Button, SkeletonBar, SkeletonRow } from './mail.styles';

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 3rem 1.5rem;
    text-align: center;
    height: 100%;
    box-sizing: border-box;
`;

const Title = styled.h4`
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: #475569;
`;

const Description = styled.p`
    margin: 0;
    font-size: 0.88rem;
    color: #94a3b8;
    max-width: 320px;
    line-height: 1.5;
`;

const IconHolder = styled.div`
    opacity: 0.4;
    color: #64748b;
`;

export type EmptyVariant =
    | 'listLoading'
    | 'empty'
    | 'emptySearch'
    | 'listError'
    | 'noSelection'
    | 'detailLoading'
    | 'detailError';

interface Props {
    variant: EmptyVariant;
    onRetry?: () => void;
    onClearSearch?: () => void;
}

/**
 * Loading and empty are deliberately separate states. AdminClients conflates them and shows
 * "Žiadni klienti" during the first fetch — for a mailbox that would read as "my inbox is
 * gone", which is alarming rather than merely wrong.
 */
const MailEmptyState: React.FC<Props> = ({ variant, onRetry, onClearSearch }) => {
    if (variant === 'listLoading') {
        return (
            <div>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                    <SkeletonRow key={i}>
                        <SkeletonBar $width="45%" $height="13px" />
                        <SkeletonBar $width="80%" $height="11px" />
                    </SkeletonRow>
                ))}
            </div>
        );
    }

    if (variant === 'detailLoading') {
        return (
            <Wrapper>
                <IconHolder>
                    <MailOpen size={40} />
                </IconHolder>
                <Description>Načítavam správu…</Description>
            </Wrapper>
        );
    }

    if (variant === 'listError' || variant === 'detailError') {
        return (
            <Wrapper>
                <IconHolder>
                    <AlertCircle size={48} />
                </IconHolder>
                <Title>
                    {variant === 'listError' ? 'Poštu sa nepodarilo načítať' : 'Správu sa nepodarilo otvoriť'}
                </Title>
                <Description>Skontrolujte pripojenie a skúste to znova.</Description>
                {onRetry && (
                    <Button type="button" $variant="secondary" onClick={onRetry} whileTap={{ scale: 0.97 }}>
                        Skúsiť znova
                    </Button>
                )}
            </Wrapper>
        );
    }

    if (variant === 'emptySearch') {
        return (
            <Wrapper>
                <IconHolder>
                    <SearchX size={48} />
                </IconHolder>
                <Title>Nič sa nenašlo</Title>
                <Description>Skúste zmeniť hľadaný výraz.</Description>
                {onClearSearch && (
                    <Button type="button" $variant="secondary" onClick={onClearSearch} whileTap={{ scale: 0.97 }}>
                        Zrušiť hľadanie
                    </Button>
                )}
            </Wrapper>
        );
    }

    if (variant === 'noSelection') {
        return (
            <Wrapper>
                <IconHolder>
                    <Mail size={48} />
                </IconHolder>
                <Title>Vyberte správu</Title>
                <Description>Vyberte správu zo zoznamu vľavo a zobrazí sa tu.</Description>
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <IconHolder>
                <Inbox size={48} />
            </IconHolder>
            <Title>Žiadne správy</Title>
            <Description>V doručenej pošte zatiaľ nie sú žiadne správy.</Description>
        </Wrapper>
    );
};

export default MailEmptyState;
